/**
 * Shared commercial-pricing primitives — units, entry shapes, per-unit calculators, markup, margin.
 *
 * Split out of the former 3.5k-line `pricing.ts` so image and video each own their registry.
 * Everything here is modality-agnostic; anything that knows seconds-vs-images lives in
 * `pricing.video.ts` / `pricing.image.ts`. `pricing.ts` stays the barrel, so no call site changed.
 *
 * NOTE the `pricing.` filename prefix: `image.ts` and `video.ts` in this directory are already
 * taken by the Replicate PROVIDER runners, which are a different concern entirely.
 */
import {
  EByteDanceImageGenerationModelType,
  ELipsyncModel,
  FluxImageGenerationModelType,
  GoogleImageGenerationModelType,
  GoogleVideoGenerationModelType,
  GrokImageGenerationModelType,
  GrokVideoGenerationModelType,
  HappyHorseVideoGenerationModelType,
  HiDreamImageGenerationModelType,
  HunyuanVideoGenerationModelType,
  IdeogramImageGenerationModelType,
  KlingAIVideoGenerationModelType,
  LtxVideoGenerationModelType,
  LumaVideoGenerationModelType,
  EMaiImageGenerationModelType,
  MinimaxImageGenerationModelType,
  MinimaxVideoGenerationModelType,
  NucleusImageGenerationModelType,
  NvidiaImageGenerationModelType,
  OpenAIImageGenerationModelType,
  OpenAIVideoGenerationModelType,
  PixverseVideoGenerationModelType,
  PrunaAIImageGenerationModelType,
  QwenImageGenerationModelType,
  RecraftImageGenerationModelType,
  EReveImageGenerationModelType,
  RunwayVideoGenerationModelType,
  SeedanceVideoGenerationModelType,
  SkyReelsVideoGenerationModelType,
  ViduVideoGenerationModelType,
  WanImageGenerationModelType,
  WavespeedVideoGenerationModelType,
} from "../../image/enums"
import type { ImageGenerationModelType, VideoGenerationModelType } from "../../image/enums"
import { blg } from "../../logging"
import { calculateKieAIKlingCost, KIEAI_KLING_PRICING, KIEAI_VEO_PRICING } from "../kie-ai/pricing"
import type { ImageGenerationModelParams } from "./schemas/image"
import type { VideoGenerationModelParams } from "./schemas/video"

// Enum for pricing units
export enum EPricingUnit {
  PER_SECOND = "per_second",
  PER_FRAME = "per_frame",
  PER_REQUEST = "per_request",
  PER_IMAGE = "per_image",
  /** LLMs — rate is per single token; `calculateCost` sums the in/out/cached legs. */
  PER_TOKEN = "per_token",
  /** TTS — rate is per single character of synthesized text. */
  PER_CHARACTER = "per_character",
}

/**
 * Per-modality parameter shapes.
 *
 * Each modality is priced from ITS OWN inputs — that is the whole point of `BaseModelPricing<T>`
 * being generic. An LLM bills three token legs at different rates, TTS bills characters, lipsync
 * bills output seconds at a resolution tier. Collapsing these into one shape would force every
 * model through the widest (and least meaningful) parameter set.
 */
export interface LlmPricingParams {
  inputTokens: number
  outputTokens: number
  /** Cached-read input tokens, billed at the discounted `cachedTextInput` leg when present. */
  cachedTokens?: number
  /** Prompt-cache write tokens, billed at 1.25x Anthropic's standard input rate for a five-minute entry. */
  cacheCreationTokens?: number
}

export interface TtsPricingParams {
  characterCount: number
}

/** Music generation billed per finished output (Suno). */
export interface MusicPricingParams {
  outputs: number
}

export interface LipsyncPricingParams {
  /** Output video length in seconds. */
  seconds: number
  /** Submitted resolution tier — lipsync rates roughly double between 480p and 720p. */
  resolution?: "480p" | "720p" | "1080p"
}

// Helper functions for common pricing calculations

// Provider-level cost entry for tracking actual API costs per provider
export interface ProviderCostEntry<T> {
  rate: number | ((params: T) => number)
  unit: EPricingUnit
  /** Completion cost and quantity require a measured output duration; absent preserves requested usage. */
  requiresActualDurationSec?: boolean
  description?: string
  calculateCost?: (rate: number, params: T) => number
}

// Base interface for all model pricing
export interface BaseModelPricing<T> {
  rate: number | ((params: T) => number)
  unit: EPricingUnit
  description?: string
  // For complex calculations beyond just rate determination
  calculateCost?: (rate: number, params: T) => number
  // Provider-level costs for margin tracking (keys: "fal", "kie-ai", "replicate", "wavespeed", "openai")
  providerCosts?: Partial<Record<string, ProviderCostEntry<T>>>
}

// Video-specific pricing interface
export interface VideoModelPricing extends BaseModelPricing<VideoGenerationModelParams> {
  fixedDuration?: number // Video-specific property
}

// Image-specific pricing interface
export type ImageModelPricing = BaseModelPricing<ImageGenerationModelParams>

// LLM / TTS / lipsync — same generic base, each parameterized by its own input shape so a model
// is priced from the things that actually drive its bill.
export type LlmModelPricing = BaseModelPricing<LlmPricingParams>
export type TtsModelPricing = BaseModelPricing<TtsPricingParams>
export type LipsyncModelPricing = BaseModelPricing<LipsyncPricingParams>
export type MusicModelPricing = BaseModelPricing<MusicPricingParams>

// Legacy alias for backward compatibility
export type ModelPricing = VideoModelPricing

// Internal Replicate costs - NEVER expose to frontend
// SkyReels V4 bills a flat per-second rate across all three modes (T2V/I2V/R2V),

/** USD a single credit is worth. Shared: both modalities convert through it. */
const CREDIT_VALUE_USD = 0.05

/** Customer USD represented by an already rounded credit charge; no second markup. */
export function convertCreditsToUsd(credits: number): number {
  return Number.parseFloat((credits * CREDIT_VALUE_USD).toFixed(6))
}

export const CONSUMER_MARKUP_MULTIPLIER = 1.2

/**
 * Convert USD cost to credits with markup
 * @param usdCost Internal cost in USD
 * @param markup Markup multiplier (e.g., 1.2 for 20% markup)
 * @returns Number of credits required
 */
export function convertUsdToCredits(usdCost: number, markup = CONSUMER_MARKUP_MULTIPLIER): number {
  const result = (usdCost * markup) / CREDIT_VALUE_USD
  return Number.parseFloat(result.toFixed(1))
}

/**
 * Calculate credits for video generation with markup applied
 * This is the user-facing credit cost including markup
 */

export function getMarginData(
  consumerCostUsd: number,
  providerCostUsd: number | null,
): { consumerPrice: number; providerCost: number | null; margin: number | null; marginPercent: number | null } {
  if (providerCostUsd === null) {
    return { consumerPrice: consumerCostUsd, providerCost: null, margin: null, marginPercent: null }
  }
  const margin = consumerCostUsd - providerCostUsd
  const marginPercent = consumerCostUsd > 0 ? (margin / consumerCostUsd) * 100 : 0
  return {
    consumerPrice: consumerCostUsd,
    providerCost: providerCostUsd,
    margin: Number.parseFloat(margin.toFixed(6)),
    marginPercent: Number.parseFloat(marginPercent.toFixed(2)),
  }
}
